import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { useServices } from "@/app/use-services";
import { errorMessage } from "@/lib/api/errors";
import type { Transits } from "@/lib/api/models/daily";
import { VedicChart } from "@/features/chart/VedicChart";
import { BackLink } from "@/ui/BackLink";
import { ErrorView } from "@/ui/ErrorView";
import { Page } from "@/ui/Page";
import { Row } from "@/ui/Row";
import { Body, Display, Label, Tiny } from "@/ui/Text";
import { computedAt, sadeSatiLine, transitLine } from "./transit-lines";

/** The sixteen divisional charts, as a menu. */
export function ChartsPage() {
  const { api } = useServices();
  const navigate = useNavigate();
  const menu = useQuery({ queryKey: ["me", "charts"], queryFn: () => api.fetchChartMenu() });
  return (
    <Page>
      <BackLink to="/life" label="Life" />
      <Label className="mt-3">Your charts</Label>
      <Display className="mt-1.5">Sixteen ways of reading one birth.</Display>
      <Body className="mt-1.5">Each divisional chart takes the same moment and reads one part of life through it. All of them are free.</Body>
      <div className="mt-4">
        {menu.isPending && <Body>Reading your charts…</Body>}
        {menu.isError && <ErrorView message={errorMessage(menu.error)} onRetry={() => void menu.refetch()} />}
        {menu.data?.map((chart) => (
          <Row key={chart.divisor} label={`${chart.name} · D${chart.divisor}`} value={chart.read_for} serif={false} onClick={() => navigate(`/life/charts/${chart.divisor}`)} />
        ))}
      </div>
    </Page>
  );
}

/** One divisional chart, drawn. */
export function VargaPage() {
  const { divisor = "1" } = useParams();
  const { api } = useServices();
  const n = Number(divisor);
  const varga = useQuery({ queryKey: ["me", "varga", n], queryFn: () => api.fetchVarga(n), enabled: Number.isInteger(n) && n > 0 });
  return (
    <Page>
      <BackLink to="/life/charts" label="Your charts" />
      {varga.isPending && <Body className="mt-3">Drawing D{divisor}…</Body>}
      {varga.isError && <ErrorView message={errorMessage(varga.error)} onRetry={() => void varga.refetch()} />}
      {varga.data && (
        <>
          <Label className="mt-3">D{varga.data.divisor}</Label>
          <Display className="mt-1.5">{varga.data.name}</Display>
          <Body className="mt-1.5">Read for {varga.data.signification}.</Body>
          <div className="mt-4">
            <VedicChart
              title={varga.data.name}
              ascendantSign={varga.data.ascendant_sign}
              placements={varga.data.placements.map((p) => ({ planet: p.planet, house: p.house, retrograde: p.retrograde }))}
            />
          </div>
          <Label className="mt-4">Placements</Label>
          {varga.data.placements.map((p) => (
            <Row
              key={p.planet}
              label={p.planet}
              serif={false}
              value={p.varga_sign === p.rasi_sign ? `${p.varga_sign} · house ${p.house}` : `${p.varga_sign} · house ${p.house} · ${p.rasi_sign} at birth`}
            />
          ))}
          <Tiny className="mt-5">Computed by an ephemeris from your birth details. Nothing on this screen is written by a model.</Tiny>
        </>
      )}
    </Page>
  );
}

/** Where the planets are now, relative to the chart. */
export function TransitsPage() {
  const { api } = useServices();
  const transits = useQuery({ queryKey: ["me", "transits"], queryFn: () => api.fetchTransits() });
  return (
    <Page>
      <BackLink to="/life" label="Life" />
      <Label className="mt-3">Right now</Label>
      <Display className="mt-1.5">Where the planets are.</Display>
      <Body className="mt-1.5">Houses are counted from your ascendant, so these are positions relative to your chart rather than the sky in general.</Body>
      <div className="mt-4">
        {transits.isPending && <Body>Reading where everything is…</Body>}
        {transits.isError && <ErrorView message={errorMessage(transits.error)} onRetry={() => void transits.refetch()} />}
        {transits.data && <TransitRows transits={transits.data} />}
      </div>
    </Page>
  );
}

function TransitRows({ transits }: { readonly transits: Transits }) {
  const saturn = transits.saturn_house_from_moon;
  return (
    <>
      {transits.planets.map((p) => (
        <Row key={p.planet} label={p.planet} serif={false} value={transitLine(p.sign, p.house_from_lagna, p.retrograde, p.conjunct_natal)} />
      ))}
      {saturn !== undefined && (
        <>
          <Label className="mt-5">From your Moon</Label>
          <Row label="Saturn" serif={false} value={sadeSatiLine(transits)} />
          {transits.jupiter_house_from_moon !== undefined && (
            <Row label="Jupiter" serif={false} value={`House ${transits.jupiter_house_from_moon} from your Moon.`} />
          )}
        </>
      )}
      <Tiny className="mt-5">{computedAt(transits)}</Tiny>
    </>
  );
}

const KIND_LABEL: Readonly<Record<string, string>> = { mantra: "Say", charity: "Give", fasting: "Keep" };

/** Zero-cost remedies for the graha running now. */
export function RemediesPage() {
  const { api } = useServices();
  const remedies = useQuery({ queryKey: ["me", "remedies"], queryFn: () => api.fetchRemedies() });
  return (
    <Page>
      <BackLink to="/life" label="Life" />
      <Label className="mt-3">Remedies</Label>
      {remedies.isPending && <Body className="mt-3">Reading what is running…</Body>}
      {remedies.isError && <ErrorView message={errorMessage(remedies.error)} onRetry={() => void remedies.refetch()} />}
      {remedies.data && (
        <>
          <Display className="mt-1.5">For {remedies.data.for_graha}, which is running now.</Display>
          <div className="mt-4">
            {remedies.data.remedies.map((r) => (
              <Row key={`${r.kind}-${r.text}`} label={KIND_LABEL[r.kind] ?? r.kind} value={r.text} />
            ))}
          </div>
          {remedies.data.never_sold && <Label className="mt-5">Nothing here costs money</Label>}
          {remedies.data.note && <Tiny className="mt-1.5">{remedies.data.note}</Tiny>}
        </>
      )}
    </Page>
  );
}
